/**
 * The deletion page's "Sign in again", checked when it comes back.
 *
 * Deleting an account needs a sign-in in the last ten minutes, so the page
 * signs the person out and asks them to sign in again as themselves. A
 * sign-in as somebody else simply opened that other person's own deletion
 * page: somebody who meant to delete one account could delete another. The
 * app refuses such a sign-in and signs it out (differentAccount, review
 * identity F7a); the web now does the same (review-2-docs request 3).
 *
 * The request is kept in sessionStorage, not in memory, because a Google
 * sign-in leaves the page and comes back to a new one. It lasts fifteen
 * minutes: a sign-in long after is an ordinary one, not this.
 */

export interface Reauth {
  address: string
  party_id: string
  at: number
}

const KEY = 'docsdone-reauth'
const LIFE_MS = 15 * 60_000

export function rememberReauth(address: string, partyID: string, now = Date.now()): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ address, party_id: partyID, at: now }))
  } catch {
    // Without storage the code sign-in is still fixed to the address; only a
    // Google round trip goes unchecked.
  }
}

export function pendingReauth(now = Date.now()): Reauth | null {
  return storedReauth(now).live
}

/**
 * The stored request: `live` while it lasts, and `expired` when one was
 * stored and has run out, which a sign-in landing on the deletion page has to
 * know (settleSession).
 */
function storedReauth(now: number): { live: Reauth | null; expired: boolean } {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return { live: null, expired: false }
    const r = JSON.parse(raw) as Partial<Reauth>
    if (typeof r.address !== 'string' || typeof r.party_id !== 'string' || typeof r.at !== 'number') {
      return { live: null, expired: false }
    }
    const reauth = { address: r.address, party_id: r.party_id, at: r.at }
    return lasts(reauth, now) ? { live: reauth, expired: false } : { live: null, expired: true }
  } catch {
    return { live: null, expired: false }
  }
}

function lasts(r: Reauth, now: number): boolean {
  return now - r.at <= LIFE_MS && now >= r.at
}

export function forgetReauth(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // Nothing to forget.
  }
}

/**
 * What to do with a session that has just been read: nothing when no sign-in
 * again was asked for or the session is not signed in, keep it when it is the
 * same person, and refuse it when it is somebody else.
 */
export function reauthVerdict(pending: Reauth | null, signedInParty: string | null): 'none' | 'same' | 'different' {
  if (!pending || !signedInParty) return 'none'
  return signedInParty === pending.party_id ? 'same' : 'different'
}

/** A session as settleSession needs to read it: signed in as a party, or not. */
export type ReadableSession = { authenticated: false } | { authenticated: true; identity: { party_id: string } }

/**
 * What the shell does with a session it has just read. `show` hands the
 * session to the shell, with what the sign-in page is fixed to (`again`) and
 * the refusal above it (`refused`); undefined leaves either as it is. `stuck`
 * is a sign-in as somebody else that could not be signed out: the shell shows
 * neither that person's pages nor the sign-in page, and the stored request is
 * kept, so the next read asks again.
 */
export type Settled<S> =
  | { kind: 'show'; session: S; again?: Reauth | null; refused?: string; leaveDeletion?: boolean }
  | { kind: 'stuck'; again: Reauth; refused: string }

/**
 * Reads the session and holds it to a pending sign-in again. A sign-in as
 * somebody else is signed out before anything is shown; when that sign-out
 * fails, the session is read again and, if it is still somebody else, it is
 * never handed to the shell. Swallowing the failure handed the refused
 * person's session to the shell, at /account/delete after a Google return,
 * which is their own deletion page with a fresh sign-in (review-3 web-3).
 *
 * `shown` is the request the sign-in page is fixed to in this page load. It
 * stands in for the stored one when storage is blocked, and, once it has run
 * out, says the page asked for a sign-in again that has expired.
 */
export async function settleSession<S extends ReadableSession>(
  read: () => Promise<S>,
  signOut: () => Promise<unknown>,
  shown: Reauth | null = null,
  now = Date.now(),
): Promise<Settled<S>> {
  const s = await read()
  const stored = storedReauth(now)
  const pending = stored.live ?? (shown && lasts(shown, now) ? shown : null)
  const verdict = reauthVerdict(pending, partyOf(s))
  if (verdict === 'different' && pending) {
    const again = { address: pending.address, party_id: pending.party_id, at: now }
    rememberReauth(again.address, again.party_id, now)
    await signOut().catch(() => undefined)
    const after = await read()
    const still = partyOf(after)
    if (still !== null && still !== pending.party_id) {
      return { kind: 'stuck', again, refused: differentAccount(pending.address) }
    }
    if (still !== null) {
      forgetReauth()
      return { kind: 'show', session: after, again: null, refused: '' }
    }
    return { kind: 'show', session: after, again, refused: differentAccount(pending.address) }
  }
  // Signed in as the person who asked, or an ordinary sign-in: either way
  // the sign-in page is no longer fixed to anybody. A Google or company
  // sign-in again comes back in a new page load, which calls neither of the
  // sign-in page's own handlers, so the page stayed fixed to the old address
  // and, after the account was deleted, asked to sign in as it to delete it
  // (review-3 web-1).
  //
  // A sign-in after the request has run out is an ordinary one, and nothing
  // can tell whose it is, so it is not shown the deletion page it asked from:
  // the page still read "Sign in as priya@...", and a sign-in as Tom landed on
  // Tom's own deletion page with a fresh sign-in (review-3 web-2).
  if (s.authenticated) {
    const expired = verdict === 'none' && (stored.expired || shown !== null)
    forgetReauth()
    return { kind: 'show', session: s, again: null, refused: '', leaveDeletion: expired }
  }
  return { kind: 'show', session: s }
}

function partyOf(s: ReadableSession): string | null {
  return s.authenticated ? s.identity.party_id : null
}

/** The app's sentence (mobile/src/features/account/words.ts differentAccount). */
export function differentAccount(address: string): string {
  return `That sign-in was for a different account. Sign in as ${address} to delete this account.`
}
